import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Opportunity } from "../types";
import { getOpportunities, getBranding } from "../api";
import { useAuth } from "../context/AuthContext";
import OpportunityCard from "../components/OpportunityCard";
import OpportunityDetail from "./OpportunityDetail";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "workflow_rejected", label: "Workflow Rejected" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

const DATE_OPTIONS = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
];

function OpportunityList() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("active");
  const [dateFilter, setDateFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [selectedOpp, setSelectedOpp] = useState<Opportunity | null>(null);
  const [headerLogoUrl, setHeaderLogoUrl] = useState<string | null>(null);

  const PAGE_SIZE = 25;

  useEffect(() => {
    getBranding()
      .then((b) => { if (b.header_logo_url) setHeaderLogoUrl(b.header_logo_url); })
      .catch(() => { /* silently use default */ });
  }, []);

  const fetchOpportunities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getOpportunities(status, PAGE_SIZE, page * PAGE_SIZE);
      let filtered = data.opportunities;

      if (dateFilter !== "all") {
        const now = new Date();
        let cutoff: Date;
        if (dateFilter === "today") {
          cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        } else if (dateFilter === "7d") {
          cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        } else {
          cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        }
        filtered = filtered.filter(
          (o) => new Date(o.created_at) >= cutoff
        );
      }

      setOpportunities(filtered);
      setTotal(data.total);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load opportunities");
    } finally {
      setLoading(false);
    }
  }, [status, dateFilter, page]);

  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);

  const handleCardClick = (opp: Opportunity) => {
    if (opp.status === "accepted") {
      navigate(`/workspace/${opp._id}`);
    } else {
      setSelectedOpp(opp);
    }
  };

  const handleDetailClose = () => {
    setSelectedOpp(null);
    fetchOpportunities();
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f0f2f5", fontFamily: "Segoe UI, sans-serif" }}>
      {/* Header */}
      <div style={{
        backgroundColor: "#1a5276", color: "white", padding: "16px 24px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {headerLogoUrl && (
            <img
              src={headerLogoUrl}
              alt="Logo"
              style={{ maxHeight: "36px", maxWidth: "140px", objectFit: "contain" }}
              onError={() => setHeaderLogoUrl(null)}
            />
          )}
          <div>
            <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>RFP Pipeline</h1>
            <span style={{ fontSize: "12px", opacity: 0.8 }}>Opportunity Review Queue</span>
          </div>
        </div>
        <div style={{ fontSize: "13px", display: "flex", alignItems: "center", gap: "12px" }}>
          <span>{user?.name}</span>
          <button
            onClick={() => navigate("/admin")}
            style={{
              padding: "6px 12px", backgroundColor: "rgba(255,255,255,0.15)",
              color: "white", border: "1px solid rgba(255,255,255,0.3)", borderRadius: "4px",
              cursor: "pointer", fontSize: "12px",
            }}
          >
            Admin
          </button>
          <button
            onClick={logout}
            style={{
              padding: "6px 12px", backgroundColor: "rgba(255,255,255,0.15)",
              color: "white", border: "1px solid rgba(255,255,255,0.3)", borderRadius: "4px",
              cursor: "pointer", fontSize: "12px",
            }}
          >
            Sign out
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ padding: "16px 24px", backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: "13px", color: "#666", marginRight: "8px" }}>Status:</span>
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setStatus(opt.value); setPage(0); }}
              style={{
                padding: "6px 14px", borderRadius: "16px", fontSize: "13px",
                border: "1px solid",
                borderColor: status === opt.value ? "#1a5276" : "#ddd",
                backgroundColor: status === opt.value ? "#1a5276" : "white",
                color: status === opt.value ? "white" : "#555",
                cursor: "pointer",
              }}
            >
              {opt.label}
            </button>
          ))}

          <span style={{ fontSize: "13px", color: "#666", marginLeft: "24px", marginRight: "8px" }}>Date:</span>
          {DATE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setDateFilter(opt.value); setPage(0); }}
              style={{
                padding: "6px 14px", borderRadius: "16px", fontSize: "13px",
                border: "1px solid",
                borderColor: dateFilter === opt.value ? "#1a5276" : "#ddd",
                backgroundColor: dateFilter === opt.value ? "#1a5276" : "white",
                color: dateFilter === opt.value ? "white" : "#555",
                cursor: "pointer",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "24px", maxWidth: "900px", margin: "0 auto" }}>
        {loading && <p style={{ color: "#666", textAlign: "center" }}>Loading opportunities...</p>}

        {error && (
          <div style={{
            backgroundColor: "#fdecea", border: "1px solid #f5c6cb", borderRadius: "6px",
            padding: "16px", color: "#c0392b", marginBottom: "16px",
          }}>
            {error}
          </div>
        )}

        {!loading && !error && opportunities.length === 0 && (
          <div style={{ textAlign: "center", padding: "48px", color: "#999" }}>
            <p style={{ fontSize: "16px" }}>No opportunities found</p>
            <p style={{ fontSize: "13px" }}>Try a different status filter or check back later.</p>
          </div>
        )}

        {opportunities.map((opp) => (
          <OpportunityCard
            key={opp._id}
            opportunity={opp}
            onClick={handleCardClick}
            showNewBadge={status === "active"}
          />
        ))}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "24px" }}>
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              style={pageBtnStyle}
            >
              Previous
            </button>
            <span style={{ padding: "8px 12px", fontSize: "13px", color: "#666" }}>
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              style={pageBtnStyle}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      {selectedOpp && (
        <OpportunityDetail opportunity={selectedOpp} onClose={handleDetailClose} />
      )}
    </div>
  );
}

const pageBtnStyle: React.CSSProperties = {
  padding: "8px 16px", border: "1px solid #ddd", borderRadius: "4px",
  backgroundColor: "white", cursor: "pointer", fontSize: "13px",
};

export default OpportunityList;
